import { ITU_CODE, ITU_DECODE } from "./code";

export type UnknownPolicy = "skip" | "throw" | "placeholder";

export interface EncodeOptions {
  /// How to handle characters not in the ITU table.
  ///   "skip"        — drop silently
  ///   "throw"       — throw with the offending character
  ///   "placeholder" — emit the literal "?" symbol (default)
  onUnknown?: UnknownPolicy;
  /// Separator between characters in the output. Default " " (one
  /// space). The single-space convention pairs with `/` for word
  /// breaks below — a common rendering for human-readable Morse.
  charSep?: string;
  /// Separator between words. Default " / " — keeps the format
  /// distinguishable from the intra-word character separator.
  wordSep?: string;
}

/// Encode a text string into a printable Morse string of dots and
/// dashes, e.g. `"SOS"` → `"... --- ..."`, `"HI THERE"` →
/// `".... .. / - .... . .-. ."`.
export function encode(text: string, options: EncodeOptions = {}): string {
  const onUnknown = options.onUnknown ?? "placeholder";
  const charSep = options.charSep ?? " ";
  const wordSep = options.wordSep ?? " / ";

  return text
    .split(/\s+/)
    .filter((word) => word.length > 0)
    .map((word) =>
      [...word]
        .map((char) => encodeChar(char, onUnknown))
        .filter((p): p is string => p !== null)
        .join(charSep),
    )
    .filter((word) => word.length > 0)
    .join(wordSep);
}

function encodeChar(char: string, onUnknown: UnknownPolicy): string | null {
  const upper = char.toUpperCase();
  const pattern = ITU_CODE[upper];
  if (pattern !== undefined) return pattern;
  if (onUnknown === "throw") {
    throw new Error(`morse_encode_unknown_char_${JSON.stringify(char)}`);
  }
  if (onUnknown === "placeholder") return ITU_CODE["?"]!;
  return null;
}

export interface DecodeOptions {
  onUnknown?: UnknownPolicy;
  charSep?: string;
  wordSep?: string;
}

/// Decode a Morse string back to text. Inverse of `encode`.
export function decode(morse: string, options: DecodeOptions = {}): string {
  const onUnknown = options.onUnknown ?? "placeholder";
  const charSep = options.charSep ?? " ";
  const wordSep = options.wordSep ?? " / ";

  return morse
    .split(wordSep)
    .map((word) =>
      word
        .split(charSep)
        .filter((p) => p.length > 0)
        .map((pattern) => decodePattern(pattern, onUnknown))
        .filter((c): c is string => c !== null)
        .join(""),
    )
    .filter((word) => word.length > 0)
    .join(" ");
}

function decodePattern(pattern: string, onUnknown: UnknownPolicy): string | null {
  const char = ITU_DECODE[pattern];
  if (char !== undefined) return char;
  if (onUnknown === "throw") {
    throw new Error(`morse_decode_unknown_pattern_${pattern}`);
  }
  if (onUnknown === "placeholder") return "?";
  return null;
}
